import React, { useState } from 'react';
import { FaTimes, FaExclamationTriangle, FaMoneyBillWave } from 'react-icons/fa';
import { dismissDuplicate } from '../services/duplicateDetectionService';

const DuplicatesModal = ({ isOpen, onClose, duplicates, onDuplicatesDismissed, type = 'rcti' }) => {
  const [dismissing, setDismissing] = useState(null);

  if (!isOpen) return null;

  const isInvoiceType = type === 'invoice';
  const titleText = isInvoiceType ? 'Duplicate Invoices Detected' : 'Duplicate Payments Detected';
  const subtitleText = isInvoiceType 
    ? 'Clients billed multiple times for the same job' 
    : 'Franchisees paid multiple times for the same job';
  const actionText = isInvoiceType
    ? 'Review and void duplicate invoices. Each duplicate represents money charged twice to the client for the same work.'
    : 'Arrange for franchisee to be invoiced for overpayment. Each duplicate represents money paid twice to the franchisee for the same work.';

  const handleDismiss = async (duplicateId) => {
    setDismissing(duplicateId);
    try {
      await dismissDuplicate(duplicateId);
      // Notify parent to refresh the list
      if (onDuplicatesDismissed) {
        onDuplicatesDismissed();
      }
    } catch (error) {
      console.error('Error dismissing duplicate:', error);
      alert('Failed to dismiss duplicate: ' + error.message);
    } finally {
      setDismissing(null);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp._seconds * 1000);
    return date.toLocaleDateString('en-AU', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(amount || 0);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl shadow-2xl ring-1 ring-gray-900/5 max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-red-600 text-white px-6 py-4 flex justify-between items-center">
          <div className="flex items-center">
            <FaExclamationTriangle className="mr-3 text-2xl" />
            <div>
              <h2 className="text-xl font-bold">{titleText}</h2>
              <p className="text-sm text-red-100">{subtitleText}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-red-200 transition-colors"
          >
            <FaTimes className="text-2xl" />
          </button>
        </div>

        {/* Alert Message */}
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 m-6 mb-4">
          <div className="flex items-start">
            <FaMoneyBillWave className="text-yellow-600 mt-1 mr-3 flex-shrink-0" />
            <div>
              <p className="font-semibold text-yellow-800">Action Required:</p>
              <p className="text-yellow-700 text-sm mt-1">
                {actionText}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 pb-6">
          {duplicates && duplicates.length > 0 ? (
            <div className="space-y-4">
              {duplicates.map((duplicate) => {
                const isDismissed = duplicate.status === 'dismissed';

                return (
                  <div
                    key={duplicate.id}
                    className={`border rounded-lg p-4 ${
                      isDismissed
                        ? 'bg-gray-50 border-gray-300 opacity-60'
                        : 'bg-red-50 border-red-300'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className={`font-semibold text-lg ${isDismissed ? 'line-through text-gray-600' : 'text-red-800'}`}>
                          Job ID: {duplicate.jobId}
                        </h3>
                        <p className="text-sm text-gray-600">
                          Franchisee: <span className="font-medium">
                            {isInvoiceType 
                              ? (duplicate.invoice1?.franchiseName || 'Unknown')
                              : (duplicate.rcti1?.franchiseeName || 'Unknown')}
                          </span>
                        </p>
                        <p className="text-sm text-gray-600">
                          Client: <span className="font-medium">
                            {isInvoiceType
                              ? (duplicate.invoice1?.householdName || 'Unknown')
                              : (duplicate.rcti1?.clientName || 'Unknown')}
                          </span>
                        </p>
                        {isDismissed && (
                          <p className="text-xs text-gray-500 mt-1">
                            Dismissed on {formatDate(duplicate.dismissedAt)}
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-600">{isInvoiceType ? 'Total Overbilling' : 'Total Overpayment'}</p>
                        <p className={`text-xl font-bold ${isDismissed ? 'text-gray-600' : 'text-red-600'}`}>
                          {formatCurrency(duplicate.totalOverbilling || duplicate.totalOverpayment)}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                      {/* First Item */}
                      <div className="bg-white rounded p-3 border border-gray-200">
                        <p className="text-xs text-gray-500 mb-2">{isInvoiceType ? 'ORIGINAL INVOICE' : 'ORIGINAL PAYMENT'}</p>
                        {isInvoiceType ? (
                          <>
                            <p className="font-medium text-sm">Invoice #{duplicate.invoice1?.invoiceNumber}</p>
                            <p className="text-sm text-gray-600">Invoice Date: {formatDate(duplicate.invoice1?.date)}</p>
                            {duplicate.jobCompletionDate && (
                              <p className="text-sm text-gray-600">Job Completed: {formatDate(duplicate.jobCompletionDate)}</p>
                            )}
                            <p className="text-sm text-gray-600">Amount: {formatCurrency(duplicate.invoice1?.amount)}</p>
                            <p className="text-sm">
                              <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                                duplicate.invoice1?.status === 'paid'
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-yellow-100 text-yellow-800'
                              }`}>
                                {duplicate.invoice1?.status || 'Unknown'}
                              </span>
                            </p>
                          </>
                        ) : (
                          <>
                            <p className="font-medium text-sm">RCTI #{duplicate.rcti1?.rctiNumber}</p>
                            <p className="text-sm text-gray-600">RCTI Date: {formatDate(duplicate.rcti1?.date)}</p>
                            {duplicate.jobCompletionDate && (
                              <p className="text-sm text-gray-600">Job Completed: {formatDate(duplicate.jobCompletionDate)}</p>
                            )}
                            <p className="text-sm text-gray-600">Amount: {formatCurrency(duplicate.rcti1?.amount)}</p>
                            <p className="text-sm">
                              <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                                duplicate.rcti1?.status === 'paid'
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-yellow-100 text-yellow-800'
                              }`}>
                                {duplicate.rcti1?.status || 'Unknown'}
                              </span>
                            </p>
                          </>
                        )}
                      </div>

                      {/* Second Item (Duplicate) */}
                      <div className="bg-white rounded p-3 border border-red-300">
                        <p className="text-xs text-red-600 mb-2 font-semibold">{isInvoiceType ? 'DUPLICATE INVOICE' : 'DUPLICATE PAYMENT'}</p>
                        {isInvoiceType ? (
                          <>
                            <p className="font-medium text-sm">Invoice #{duplicate.invoice2?.invoiceNumber}</p>
                            <p className="text-sm text-gray-600">Invoice Date: {formatDate(duplicate.invoice2?.date)}</p>
                            {duplicate.jobCompletionDate && (
                              <p className="text-sm text-gray-600">Job Completed: {formatDate(duplicate.jobCompletionDate)}</p>
                            )}
                            <p className="text-sm text-gray-600">Amount: {formatCurrency(duplicate.invoice2?.amount)}</p>
                            <p className="text-sm">
                              <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                                duplicate.invoice2?.status === 'paid'
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-yellow-100 text-yellow-800'
                              }`}>
                                {duplicate.invoice2?.status || 'Unknown'}
                              </span>
                            </p>
                          </>
                        ) : (
                          <>
                            <p className="font-medium text-sm">RCTI #{duplicate.rcti2?.rctiNumber}</p>
                            <p className="text-sm text-gray-600">RCTI Date: {formatDate(duplicate.rcti2?.date)}</p>
                            {duplicate.jobCompletionDate && (
                              <p className="text-sm text-gray-600">Job Completed: {formatDate(duplicate.jobCompletionDate)}</p>
                            )}
                            <p className="text-sm text-gray-600">Amount: {formatCurrency(duplicate.rcti2?.amount)}</p>
                            <p className="text-sm">
                              <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                                duplicate.rcti2?.status === 'paid'
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-yellow-100 text-yellow-800'
                              }`}>
                                {duplicate.rcti2?.status || 'Unknown'}
                              </span>
                            </p>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Dismiss Button */}
                    {!isDismissed && (
                      <div className="flex justify-end">
                        <button
                          onClick={() => handleDismiss(duplicate.id)}
                          disabled={dismissing === duplicate.id}
                          className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {dismissing === duplicate.id ? 'Closing...' : 'Close'}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <p className="text-lg">No duplicate {isInvoiceType ? 'invoices' : 'payments'} found</p>
              <p className="text-sm mt-2">All {isInvoiceType ? 'Invoices' : 'RCTIs'} are unique</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex justify-between items-center">
          <p className="text-sm text-gray-600">
            {duplicates && duplicates.length > 0
              ? `${duplicates.filter(d => d.status === 'active').length} active duplicate(s) | ${duplicates.filter(d => d.status === 'dismissed').length} dismissed`
              : 'No duplicates'}
          </p>
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default DuplicatesModal;
